import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import {
  LearningMaterialSummary,
  useAcceptUserTermsFunctionRequest,
  useGetLearningMaterialFunctionRequest,
  useGetUserTermsStatusFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import Button from "shared/UI/Buttons/Button";
import { TermKeys } from "core/enums/enums";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { FloatingPopover } from "UIPalette/FloatingPopover/FloatingPopover";
import { ResourceCard } from "UIPalette/ResourceCard/ResourceCard";
import { COACHMARK_DELAY_MS } from "../../enums/supportMoments/supportMoments";
import { RecommendedResourceCoachmark } from "../Coachmarks/RecommededResourceCoachMark/RecommendedResourceCoachmark";
import { AudioPlayer } from "../AudioPlayer/AudioPlayer";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";

interface RecommendedResourceProps {
  resource?: LearningMaterialSummary;
  onNotFoundCtaClick: () => void;
  onDone: () => void;
}

export const RecommendedResource = ({ resource, onNotFoundCtaClick, onDone }: RecommendedResourceProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "supportMoments.recommendedResource" });
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [showCoachmark, setShowCoachmark] = useState(false);
  const xClientCode = ClientInfoState.getCode();

  const {
    data: coachmarkStatus,
    isLoading: isLoadingCoachmarkStatus,
    mutate: mutateCoachmarkStatus,
  } = useGetUserTermsStatusFunctionRequest({ xClientCode, key: TermKeys.SupportMomentsTutorial });
  const { trigger: triggerAcceptCoachmark } = useAcceptUserTermsFunctionRequest({ xClientCode });
  const { data: audioPlayerArticle } = useGetLearningMaterialFunctionRequest(
    { id: resource?.id },
    { shouldFetch: !!resource, revalidateOnMountIfStale: false, showErrorIfStale: false }
  );
  const isDesktop = useMatchMedia(ResponsiveBreakpoints.Desktop);

  useEffect(() => {
    if (!resource || isPlayerOpen || isLoadingCoachmarkStatus || coachmarkStatus?.accepted) {
      return;
    }

    const timeout = setTimeout(() => {
      trackEvents(EventNames.SUPPORT_MOMENTS_COACHMARK_VIEWED, { step: "3" });
      setShowCoachmark(true);
    }, COACHMARK_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [resource, isPlayerOpen, isLoadingCoachmarkStatus, coachmarkStatus?.accepted]);

  if (!resource) {
    return (
      <div className="text-center space-y-4">
        <p>{t("notFound.message")}</p>
        <Button text={t("notFound.cta")} onClick={onNotFoundCtaClick} />
      </div>
    );
  }

  const handleCoachmarkDone = async (dismiss: () => void) => {
    trackEvents(EventNames.SUPPORT_MOMENTS_COACH_MARK_COMPLETED, { step: "3" });
    setShowCoachmark(false);
    dismiss();

    await triggerAcceptCoachmark({ query: { key: TermKeys.SupportMomentsTutorial } });
    mutateCoachmarkStatus();
  };

  const handleOpenResource = () => setIsPlayerOpen(true);

  const handlePlayerClose = () => {
    setIsPlayerOpen(false);
  };

  const handlePlayerDone = () => {
    setIsPlayerOpen(false);
    onDone();
  };

  const playerIsOpen = isPlayerOpen && audioPlayerArticle;

  return (
    <>
      {!(isDesktop && playerIsOpen) && (
        <>
          <p>{t("bodyText")}</p>
          <FloatingPopover
            initialOpenState={showCoachmark}
            borderRadius={20}
            trigger={(props) => <ResourceCard ref={props.ref} item={resource} className="!h-[370px]" handleClick={handleOpenResource} delayExperienceModal />}
          >
            {({ dismiss }) => <RecommendedResourceCoachmark step={3} totalSteps={3} onDone={() => handleCoachmarkDone(dismiss)} />}
          </FloatingPopover>
        </>
      )}
      {isPlayerOpen && audioPlayerArticle && (
        <div>
          <AudioPlayer
            resource={{
              title: audioPlayerArticle.title,
              imageUrl: resource.image.url,
              src: audioPlayerArticle.link ?? "",
            }}
            onClose={handlePlayerClose}
            onDone={handlePlayerDone}
          />
        </div>
      )}
    </>
  );
};
